<template>
  <Teleport to="body">
    <Transition name="reminder-modal">
      <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" @click="handleClose"></div>
        <div class="relative flex w-full max-w-3xl max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
          <div class="shrink-0 border-b border-slate-200 bg-gradient-to-r from-blue-50/50 via-white to-orange-50/30 px-6 py-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <h3 class="mt-2 text-2xl font-semibold text-slate-900">{{ titleText }}</h3>
                <!-- <p class="mt-1 text-sm text-slate-500">复用账单/方案提醒能力，自定义提醒内容、接收人、重复策略与跳转动作。</p> -->
              </div>
              <button
                type="button"
                class="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600"
                @click="handleClose"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-6">
            <div class="max-w-3xl space-y-6">
              <section class="rounded-xl border border-slate-200 bg-white p-5">
                <div class="mb-5 flex items-center justify-between">
                  <div>
                    <h4 class="text-base font-semibold text-slate-900">提醒内容</h4>
                    <!-- <p class="mt-1 text-sm text-slate-500">定义标题、正文和接收人，支持多人同时接收。</p> -->
                  </div>
                  <span class="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">核心配置</span>
                </div>

                <div class="grid gap-5">
                  <div>
                    <label class="mb-2 block text-sm font-medium text-slate-700">提醒标题 <span class="text-red-500">*</span></label>
                    <el-input
                      v-model="form.title"
                      maxlength="64"
                      show-word-limit
                      placeholder="例如：跟进客户续签资料"
                      :class="{'is-error': fieldErrors.title}"
                      @blur="validateTitle"
                    />
                    <p v-if="fieldErrors.title" class="mt-1.5 text-xs text-red-500">{{ fieldErrors.title }}</p>
                  </div>
                  <div>
                    <label class="mb-2 block text-sm font-medium text-slate-700">提醒内容</label>
                    <el-input
                      v-model="form.content"
                      type="textarea"
                      :rows="4"
                      maxlength="300"
                      show-word-limit
                      resize="none"
                      placeholder="补充提醒背景、执行要求或处理说明"
                    ></el-input>
                  </div>
                  <div>
                    <label class="mb-2 block text-sm font-medium text-slate-700">提醒人员 <span class="text-red-500">*</span></label>
                    <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div class="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p class="text-sm font-medium text-slate-800">提醒自己</p>
                          <p class="mt-1 text-xs text-slate-500">将提醒任务发送给自己的账号</p>
                        </div>
                        <el-switch v-model="form.remindSelf" />
                      </div>

                      <div class="mt-4">
                        <p class="mb-2 text-sm font-medium text-slate-800">提醒他人</p>
                        <UserSelector
                          v-model="form.receiverUserIds"
                          multiple
                          placeholder="点击选择提醒人员"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section class="rounded-xl border border-slate-200 bg-white p-5">
                <div class="mb-5">
                  <h4 class="text-base font-semibold text-slate-900">时间与重复</h4>
                  <!-- <p class="mt-1 text-sm text-slate-500">控制首次提醒、重复节奏和截止关闭规则。</p> -->
                </div>

                <div class="grid gap-5 md:grid-cols-2">
                  <div>
                    <label class="mb-2 block text-sm font-medium text-slate-700">首次提醒时间 <span class="text-red-500">*</span></label>
                    <el-date-picker
                      v-model="form.firstSendTime"
                      type="datetime"
                      value-format="YYYY-MM-DDTHH:mm:ss"
                      format="YYYY-MM-DD HH:mm"
                      class="w-full"
                      placeholder="请选择首次提醒时间"
                      :class="{'is-error': fieldErrors.firstSendTime}"
                      @blur="validateFirstSendTime"
                      @change="validateFirstSendTime"
                    />
                    <p v-if="fieldErrors.firstSendTime" class="mt-1.5 text-xs text-red-500">{{ fieldErrors.firstSendTime }}</p>
                  </div>
                  <div>
                    <label class="mb-2 block text-sm font-medium text-slate-700">截止时间</label>
                    <el-date-picker
                      v-model="form.deadlineTime"
                      type="datetime"
                      value-format="YYYY-MM-DDTHH:mm:ss"
                      format="YYYY-MM-DD HH:mm"
                      class="w-full"
                      placeholder="可选，不设置则长期有效"
                      :class="{'is-error': fieldErrors.deadlineTime}"
                      @blur="validateDeadlineTime"
                      @change="validateDeadlineTime"
                    />
                    <p v-if="fieldErrors.deadlineTime" class="mt-1.5 text-xs text-red-500">{{ fieldErrors.deadlineTime }}</p>
                  </div>
                </div>

                <div class="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div class="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p class="text-sm font-medium text-slate-800">连续提醒</p>
                    </div>
                    <el-switch v-model="form.repeatEnabled" />
                  </div>

                  <div v-if="form.repeatEnabled" class="mt-4 grid gap-4 md:grid-cols-[180px_160px_1fr]">
                    <div>
                      <label class="mb-2 block text-sm font-medium text-slate-700">重复间隔</label>
                      <el-input-number v-model="form.repeatIntervalValue" :min="1" :max="9999" class="w-full" controls-position="right" />
                    </div>
                    <div>
                      <label class="mb-2 block text-sm font-medium text-slate-700">间隔单位</label>
                      <el-select v-model="form.repeatIntervalUnit" class="w-full">
                        <el-option label="分钟" value="MINUTE" />
                        <el-option label="小时" value="HOUR" />
                        <el-option label="天" value="DAY" />
                      </el-select>
                    </div>
                    <div>
                      <label class="mb-2 block text-sm font-medium text-slate-700">最大提醒次数</label>
                      <el-input-number v-model="form.maxReminderCount" :min="1" :max="9999" class="w-full" controls-position="right" />
                    </div>
                  </div>
                </div>

                <div class="mt-5 flex items-center justify-between rounded-2xl border border-slate-200 p-4">
                  <div>
                    <p class="text-sm font-medium text-slate-800">到期自动关闭</p>
                  </div>
                  <el-switch v-model="form.autoCloseAtDeadline" :disabled="!form.deadlineTime" />
                </div>
              </section>
            </div>
          </div>

          <div class="shrink-0 flex items-center justify-between gap-4 border-t border-slate-200 bg-white px-6 py-4">
            <p class="text-sm text-slate-500">{{ footerHint }}</p>
            <div class="flex items-center gap-3">
              <button type="button" class="btn-secondary" @click="handleClose">取消</button>
              <button type="button" class="btn-primary inline-flex items-center gap-2" :disabled="saving" @click="handleSubmit">
                <svg v-if="saving" class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                {{ saving ? '保存中...' : submitText }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import UserSelector from '@/components/UserSelector/index.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  detail: {
    type: Object,
    default: null
  },
  mode: {
    type: String,
    default: 'create'
  },
  saving: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:visible', 'submit'])

const userStore = useUserStore()

const form = reactive(createEmptyForm())
const fieldErrors = reactive({
  title: '',
  firstSendTime: '',
  deadlineTime: ''
})

const titleText = computed(() => (props.mode === 'edit' ? '编辑提醒' : '新建提醒'))
const submitText = computed(() => (props.mode === 'edit' ? '保存修改' : '创建提醒'))
const footerHint = computed(() => (props.mode === 'edit' ? '保存后会同步更新现有提醒任务。' : '创建后立即按定义生成对应接收人的提醒任务。'))


watch(
  () => props.visible,
  async (visible) => {
    if (!visible) return
    resetForm()
    if (props.detail) {
      Object.assign(form, buildFormModel(props.detail))
    }
  },
  { immediate: true }
)

function createEmptyForm() {
  return {
    title: '',
    content: '',
    remindSelf: false,
    receiverUserIds: [],
    firstSendTime: '',
    deadlineTime: '',
    autoCloseAtDeadline: false,
    repeatEnabled: false,
    repeatIntervalValue: 1,
    repeatIntervalUnit: 'DAY',
    maxReminderCount: 5
  }
}

function resetForm() {
  Object.assign(form, createEmptyForm())
  resetFieldErrors()
}

function buildFormModel(detail) {
  const repeat = normalizeRepeatInterval(detail.repeatIntervalMinutes)
  const currentUserId = userStore.userInfo?.userId
  const receiverIds = (detail.receivers || []).map(item => item.userId)
  return {
    title: detail.title || '',
    content: detail.content || '',
    remindSelf: currentUserId ? receiverIds.includes(currentUserId) : false,
    receiverUserIds: receiverIds.filter(id => id !== currentUserId),
    firstSendTime: normalizeDateTime(detail.firstSendTime),
    deadlineTime: normalizeDateTime(detail.deadlineTime),
    autoCloseAtDeadline: Boolean(detail.autoCloseAtDeadline),
    repeatEnabled: Boolean(detail.repeatEnabled),
    repeatIntervalValue: repeat.repeatIntervalValue,
    repeatIntervalUnit: repeat.repeatIntervalUnit,
    maxReminderCount: detail.maxReminderCount || 5
  }
}

function buildSubmitPayload(includeId) {
  const repeatIntervalMinutes = form.repeatEnabled
    ? toRepeatIntervalMinutes(form.repeatIntervalValue, form.repeatIntervalUnit)
    : null

  const currentUserId = userStore.userInfo?.userId
  const allReceiverIds = [...form.receiverUserIds]
  if (form.remindSelf && currentUserId) {
    allReceiverIds.unshift(currentUserId)
  }

  const payload = {
    title: form.title.trim(),
    content: form.content?.trim() || null,
    receiverUserIds: allReceiverIds,
    firstSendTime: normalizeDateTime(form.firstSendTime),
    deadlineTime: normalizeDateTime(form.deadlineTime),
    autoCloseAtDeadline: Boolean(form.deadlineTime && form.autoCloseAtDeadline),
    repeatEnabled: form.repeatEnabled,
    repeatIntervalMinutes,
    maxReminderCount: form.repeatEnabled ? Number(form.maxReminderCount || 1) : null
  }

  if (includeId && props.detail?.id) {
    payload.id = props.detail.id
  }
  return payload
}

function normalizeRepeatInterval(minutes) {
  if (!minutes || minutes < 60) {
    return {
      repeatIntervalValue: minutes || 1,
      repeatIntervalUnit: 'MINUTE'
    }
  }
  if (minutes % 1440 === 0) {
    return {
      repeatIntervalValue: minutes / 1440,
      repeatIntervalUnit: 'DAY'
    }
  }
  if (minutes % 60 === 0) {
    return {
      repeatIntervalValue: minutes / 60,
      repeatIntervalUnit: 'HOUR'
    }
  }
  return {
    repeatIntervalValue: minutes,
    repeatIntervalUnit: 'MINUTE'
  }
}

function toRepeatIntervalMinutes(value, unit) {
  const numericValue = Number(value || 1)
  if (unit === 'DAY') return numericValue * 1440
  if (unit === 'HOUR') return numericValue * 60
  return numericValue
}

function normalizeDateTime(value) {
  if (!value) return null
  return String(value).replace(' ', 'T')
}

function validateTitle() {
  fieldErrors.title = form.title.trim() ? '' : '请输入提醒标题'
}

function validateFirstSendTime() {
  if (!form.firstSendTime) {
    fieldErrors.firstSendTime = '请选择首次提醒时间'
  } else {
    fieldErrors.firstSendTime = ''
    validateDeadlineTime()
  }
}

function validateDeadlineTime() {
  if (form.deadlineTime && form.firstSendTime) {
    const deadline = new Date(form.deadlineTime).getTime()
    const first = new Date(form.firstSendTime).getTime()
    fieldErrors.deadlineTime = deadline < first ? '截止时间不能早于首次提醒时间' : ''
  } else {
    fieldErrors.deadlineTime = ''
  }
}

function resetFieldErrors() {
  fieldErrors.title = ''
  fieldErrors.firstSendTime = ''
  fieldErrors.deadlineTime = ''
}

function validateForm() {
  if (!form.title.trim()) {
    ElMessage.warning('请输入提醒标题')
    return false
  }
  if (!form.firstSendTime) {
    ElMessage.warning('请选择首次提醒时间')
    return false
  }
  if (!form.remindSelf && !form.receiverUserIds.length) {
    ElMessage.warning('请至少选择一位提醒人员')
    return false
  }
  if (form.deadlineTime && new Date(form.deadlineTime).getTime() < new Date(form.firstSendTime).getTime()) {
    ElMessage.warning('截止时间不能早于首次提醒时间')
    return false
  }
  if (form.repeatEnabled) {
    if (!Number(form.repeatIntervalValue) || Number(form.repeatIntervalValue) < 1) {
      ElMessage.warning('请设置正确的重复间隔')
      return false
    }
    if (!Number(form.maxReminderCount) || Number(form.maxReminderCount) < 1) {
      ElMessage.warning('请设置正确的最大提醒次数')
      return false
    }
  }
  return true
}

function handleClose() {
  emit('update:visible', false)
}

function handleSubmit() {
  if (!validateForm()) return
  const includeId = props.mode === 'edit'
  emit('submit', buildSubmitPayload(includeId))
}
</script>

<style scoped>
:deep(.el-input.is-error .el-input__wrapper) {
  box-shadow: 0 0 0 1px #ef4444 inset;
}

:deep(.el-date-editor.is-error .el-input__wrapper) {
  box-shadow: 0 0 0 1px #ef4444 inset;
}

.reminder-modal-enter-active,
.reminder-modal-leave-active {
  transition: opacity 0.22s ease;
}

.reminder-modal-enter-active .relative > .relative,
.reminder-modal-leave-active .relative > .relative {
  transition: transform 0.24s ease, opacity 0.24s ease;
}

.reminder-modal-enter-from,
.reminder-modal-leave-to {
  opacity: 0;
}

.reminder-modal-enter-from .relative > .relative,
.reminder-modal-leave-to .relative > .relative {
  transform: translateY(12px) scale(0.985);
  opacity: 0;
}
</style>
